import React, { useState, useEffect } from 'react';
import { 
  Grid, LinearProgress, Typography, Paper, Box, Chip, Button } from '@mui/material';
import { Star, Grade, ShoppingCart } from '@mui/icons-material';
import { collection, query, onSnapshot, doc } from 'firebase/firestore';
import { db, useFirebaseAuthentication } from 'lib/firebase';
import { Helmet } from 'react-helmet-async';
import { getUserPoints } from "lib/purchaseUtils"
import { getUserLevelInfo } from "../../../utils/xpUtils"
import { getNameFromFirebaseUid } from "../../../utils/userMapping"
import { useNavigate } from 'react-router-dom';
import { convertTimestampToString } from "./utils"

import DesktopChoresView from './DesktopChoresView';
import AddEditChoreDialog from './AddEditChoreDialog';
import SiteWrapper from "../../home-pages/SiteWrapper"
import AddSpeedDial from "./AddSpeedDial"
import AddOtherTasksDialog from "./AddOtherTasksDialog"

export const assigneeOptions = [
  "Ryan",
  "Leah",
  "Samantha",
  "Connor",
  "Marie",
  "All",
  "All Kids",
]

export const assignees = ["Ryan", "Leah", "Samantha", "Connor", "Marie"]

export const avatars = {
  ryan: `/images/ryan2.png`,
  leah: `/images/leah2.png`,
  samantha: `/images/samantha2.png`,
  connor: `/images/connor2.png`,
  marie: `/images/marie2.png`,
}

const initChore = {
  id: null,
  description: "",
  assignee: "",
  frequency: "daily",
}

const ChoresView = () => {
  const navigate = useNavigate()
  const [points, setPoints] = useState(0)
  const [levelInfo, setLevelInfo] = useState({
    level: 1,
    currentXP: 0,
    nextLevelXP: 50,
    progressPercentage: 0
  })
  const [currentUserName, setCurrentUserName] = useState('')
  const [chores, setChores] = useState([])
  const [openDialog, setOpenDialog] = useState(false)
  const [newChore, setNewChore] = useState(initChore)
  const [isLoading, setIsLoading] = useState(true)
  const [openOtherDialog, setOpenOtherDialog] = useState(false)
  const [otherDialogType, setOtherDialogType] = useState("")

  const authUser = useFirebaseAuthentication()

  const handleOtherDialogOpen = (type) => {
    setOtherDialogType(type)
    setOpenOtherDialog(true)
  }

  useEffect(() => {
    const loadUserData = async () => {
      if (authUser && authUser.uid) {
        try {
          // Get user's display name from UID
          const userName = getNameFromFirebaseUid(authUser.uid)
          setCurrentUserName(userName || 'Unknown User')
          
          // Load user points initially
          const userPoints = await getUserPoints(authUser.uid)
          setPoints(userPoints)
          
          // Calculate and set level info
          const levelData = getUserLevelInfo(userPoints)
          setLevelInfo(levelData)
          
          // Set up real-time listener for user points
          const userPointsRef = doc(db, "userPoints", authUser.uid)
          const unsubscribePoints = onSnapshot(userPointsRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
              const pointsData = docSnapshot.data()
              const newPoints = pointsData.points || 0
              setPoints(newPoints)
              
              // Update level info when points change
              const newLevelData = getUserLevelInfo(newPoints)
              setLevelInfo(newLevelData)
            }
          }, (error) => {
            if (error.code === 'permission-denied') {
            } else {
            }
          })
          
          // Return cleanup function for points listener only
          return () => unsubscribePoints()
        } catch (error) {
        }
      }
    }

    loadUserData()
  }, [authUser])

  // add useEffect to listen to firestore

  useEffect(() => {
    const q = query(collection(db, "chores"))
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const items = []
      querySnapshot.forEach((doc) => {
        const choreData = doc.data()
        const lastString = convertTimestampToString(choreData.last)
        const last = lastString ? new Date(lastString) : null
        items.push({
          ...choreData,
          id: doc.id,
          last,
        })
      })

      setChores(items)
      setIsLoading(false)
    }, (error) => {
      if (error.code === 'permission-denied') {
      } else {
      }
      setIsLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const choresByAssignee = chores.reduce((acc, chore) => {
    acc[chore.assignee] = [...(acc[chore.assignee] || []), chore]
    return acc
  }, {})

  const handleDialogOpen = () => {
    setOpenDialog(true)
  }

  const handleDialogClose = () => {
    setOpenDialog(false)
  }

  return (
    <SiteWrapper>
      <div className="main-page-wrapper">
        <Helmet>
          <title>Chores</title>
        </Helmet>
        
        {/* Enhanced Points and Level Display */}
        <Box sx={{ p: 2, mb: 3 }}>
          <Paper 
            elevation={4}
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                {/* User Name Display */}
                {currentUserName && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ opacity: 0.9 }}>
                      {currentUserName}
                    </Typography>
                  </Box>
                )}
                
                {/* Points Display */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Star sx={{ color: '#ffd700', fontSize: 28 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {points} Points
                  </Typography>
                </Box>
                
                {/* Level Display */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Grade sx={{ color: '#ffd700', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Level {levelInfo.level}
                  </Typography>
                  {levelInfo.isMaxLevel && (
                    <Chip 
                      label="MAX" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#ffd700', 
                        color: '#000',
                        fontWeight: 'bold'
                      }} 
                    />
                  )}
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {/* Redeem Button */}
                <Box sx={{ mb: 2, textAlign: { xs: 'left', md: 'right' } }}>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCart />}
                    onClick={() => navigate('/chorestore')}
                    sx={{
                      bgcolor: '#ffd700',
                      color: '#000',
                      fontWeight: 'bold',
                      borderRadius: 3,
                      px: 3,
                      py: 1,
                      '&:hover': {
                        bgcolor: '#ffed4e',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    Redeem Points
                  </Button>
                </Box>
                
                {/* XP Progress */}
                {!levelInfo.isMaxLevel && (
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      XP Progress: {levelInfo.currentXP}/{levelInfo.nextLevelXP}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={levelInfo.progressPercentage}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#ffd700',
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        opacity: 0.8, 
                        mt: 0.5,
                        display: 'block' 
                      }}
                    >
                      {Math.round(levelInfo.progressPercentage)}% to next level
                    </Typography>
                  </Box>
                )}
                
                {levelInfo.isMaxLevel && (
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    🎉 Congratulations! You've reached the maximum level!
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* <Button ref={targetRef} variant="contained" onClick={onButtonClick}>Test</Button>
        <br />
        <Button variant="contained" onClick={addFamily}>Add Family</Button>
        <br />
        <Button variant="contained" onClick={onAddTestFamily}>Add Test Family</Button>
        <br />
        <Button variant="contained" onClick={onAddPoints}>Add Points</Button>
        <br /> */}
        {/* {showGames && (
          <Grid container direction="row">
            {games.map((g) => (
              <Grid item xs={6} sm={4} md={2}>
                <GameIcons
                  status={g.status}
                  imageSrc={`${process.env.PUBLIC_URL}/images/${g.imageSrc}`}
                  points={g.points}
                  name={g.name}
                />
              </Grid>
            ))}
          </Grid>
        )} */}

        <DesktopChoresView choresByAssignee={choresByAssignee} />
        {isLoading && <LinearProgress sx={{ margin: "0px 50px" }} />}
      </div>

      {/* <Grid
        container
        spacing={3}
        justifyContent="space-between"
        style={{ padding: 5 }}
      >
        <Grid item container direction="row">
          <Link href="/advenchore" underline="none">
            <Box
              component="img"
              src={"/images/book.svg"}
              alt="book"
              sx={{
                width: 125,
                height: 125,
              }}
            />
            {points} Points
          </Link>
        </Grid>
      </Grid> */}

      {/* <Tooltip title="Advenchore!" arrow>
        <Fab
          color="primary"
          aria-label="add"
          onClick={showStory}
          style={{ position: "fixed", bottom: 20, left: 20 }}
        >
          <Box
            component="img"
            src={"/images/book.svg"}
            alt="book"
            sx={{
              width: 40,
              height: 40,
            }}
          />
        </Fab>
      </Tooltip> */}
      <div
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
        }}
      >
        {/* Commented out for now - maybe we'll bring these back later:
        onAsNeeded={() => handleOtherDialogOpen("asNeeded")}
        onImprove={() => handleOtherDialogOpen("improvement")}
        onScreenList={() => handleOtherDialogOpen("screens")}
        */}
        <AddSpeedDial
          onAddChore={handleDialogOpen}
        />
      </div>
      <AddEditChoreDialog
        open={openDialog}
        handleClose={handleDialogClose}
        chore={initChore}
        setChore={setNewChore}
      />
      <AddOtherTasksDialog
        open={openOtherDialog}
        handleClose={() => setOpenOtherDialog(false)}
        type={otherDialogType}
        chore={initChore}
        setChore={setNewChore}
      />
      {/* <LottieAnimation targetRef={targetRef}/> */}
    </SiteWrapper>
  )
}

export default ChoresView;
